// ROOM 404: Live Durable Object Chatroom Interface
// Hibernating WebSockets, single-room active subscription, cursor pagination, and host controls.

import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  Clock,
  Image as ImageIcon,
  MoreVertical,
  Send,
  Shield,
  Smile,
  Users,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiRequest, resolveApiUrl } from '../services/api';
import { chatSocket, type SocketStatus } from '../services/socket';
import type { ChatMessage, Room } from '../types';

interface RoomChatProps {
  roomId: string;
  onBack: () => void;
  onOpenReport: (messageId?: string) => void;
}

export const RoomChat: React.FC<RoomChatProps> = ({ roomId, onBack, onOpenReport }) => {
  const { session, isStaff } = useAuth();
  const [room, setRoom] = useState<Room | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [socketStatus, setSocketStatus] = useState<SocketStatus>('disconnected');
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<{ createdAt: number; id: string } | null>(null);
  const [slowModeSecondsLeft, setSlowModeSecondsLeft] = useState(0);
  const [chatError, setChatError] = useState<string | null>(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // 1. Fetch Room metadata & Initial Chat History
  const loadRoomAndHistory = async () => {
    try {
      const roomsData = await apiRequest<{ rooms: Room[] }>('/api/rooms');
      const current = roomsData.rooms.find((r) => r.id === roomId) || null;
      setRoom(current);

      const historyData = await apiRequest<{
        messages: ChatMessage[];
        hasMore: boolean;
        nextCursor: { createdAt: number; id: string } | null;
      }>(`/api/chat/${roomId}/history?limit=40`);

      setMessages(historyData.messages);
      setHasMore(historyData.hasMore);
      setNextCursor(historyData.nextCursor);
    } catch (err: any) {
      setChatError(err.message || 'Failed to load chat history');
    } finally {
      setLoadingHistory(false);
    }
  };

  // 2. Connect to Room's Durable Object WebSocket
  useEffect(() => {
    loadRoomAndHistory();

    const unsubscribe = chatSocket.subscribe({
      onStatusChange: (status) => setSocketStatus(status),
      onMessage: (newMsg) => {
        setMessages((prev) => {
          // Prevent duplicates
          if (prev.some((m) => m.id === newMsg.id)) return prev;
          return [...prev, newMsg];
        });
      },
      onError: (err) => {
        setChatError(err.message);
        if (err.code === 'SLOW_MODE') {
          setSlowModeSecondsLeft(15);
        }
      },
      onRevoked: (reason) => {
        setChatError(`Connection restricted: ${reason}`);
      },
    });

    chatSocket.connectToRoom(roomId);

    return () => {
      unsubscribe();
      chatSocket.disconnect();
    };
  }, [roomId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Slow mode countdown timer
  useEffect(() => {
    if (slowModeSecondsLeft <= 0) return;
    const interval = setInterval(() => {
      setSlowModeSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [slowModeSecondsLeft]);

  // Load older messages (Cursor Pagination)
  const handleLoadMore = async () => {
    if (!nextCursor || loadingHistory) return;
    try {
      const historyData = await apiRequest<{
        messages: ChatMessage[];
        hasMore: boolean;
        nextCursor: { createdAt: number; id: string } | null;
      }>(
        `/api/chat/${roomId}/history?cursorCreatedAt=${nextCursor.createdAt}&cursorId=${nextCursor.id}&limit=30`
      );

      setMessages((prev) => [...historyData.messages, ...prev]);
      setHasMore(historyData.hasMore);
      setNextCursor(historyData.nextCursor);
    } catch {
      // Ignored
    }
  };

  // Send message
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim() || slowModeSecondsLeft > 0) return;

    try {
      chatSocket.sendMessage(inputText.trim(), undefined, selectedAttachmentId || undefined);
      setInputText('');
      setSelectedAttachmentId(null);
      setChatError(null);

      if (room?.slow_mode_seconds) {
        setSlowModeSecondsLeft(room.slow_mode_seconds);
      }
    } catch (err: any) {
      setChatError(err.message || 'Failed to send message');
    }
  };

  // Image attachment upload
  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setChatError('Only images are allowed as chat attachments');
      return;
    }

    if (file.size > 5242880) {
      setChatError('Image size exceeds 5 MB limit');
      return;
    }

    setUploadingAttachment(true);
    setChatError(null);

    try {
      const buffer = await file.arrayBuffer();
      const res = await apiRequest<{ success: boolean; attachmentId: string }>(
        `/api/chat/${roomId}/attachments`,
        {
          method: 'POST',
          body: buffer,
          headers: { 'Content-Type': file.type },
        }
      );

      setSelectedAttachmentId(res.attachmentId);
    } catch (err: any) {
      setChatError(err.message || 'Attachment upload failed');
    } finally {
      setUploadingAttachment(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Toggle reaction
  const handleReaction = async (messageId: string, emoji: string) => {
    try {
      await apiRequest(`/api/chat/messages/${messageId}/react`, {
        method: 'POST',
        body: { reaction: emoji },
      });
    } catch {
      // Ignored
    }
  };

  const isRoomHost =
    room?.owner_id === session?.userId ||
    isStaff ||
    session?.roles.includes('room_host');

  return (
    <div className="flex flex-col h-[calc(100vh-60px)] pb-14 max-w-md mx-auto bg-[#090a0f]">
      {/* Room Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-[#0c0e17] border-b border-[#1f2433] z-10">
        <div className="flex items-center space-x-2">
          <button
            onClick={onBack}
            className="p-1 rounded-lg text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center space-x-1.5">
              <h2 className="text-sm font-bold text-white tracking-tight">{room?.name || 'Room Chat'}</h2>
              <div
                className={`w-1.5 h-1.5 rounded-full ${
                  socketStatus === 'connected' ? 'bg-emerald-400' : 'bg-amber-400 animate-pulse'
                }`}
              />
            </div>
            <p className="text-[10px] text-gray-400 font-mono">
              {room?.member_count || 1} members • {room?.category || 'general'}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-1">
          {room?.posting_mode === 'announcement_only' && (
            <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
              Announcements Only
            </span>
          )}
          <button
            onClick={() => onOpenReport(roomId)}
            className="p-1 text-gray-500 hover:text-amber-400 transition-colors"
            title="Report Room"
          >
            <Shield className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Error banner */}
      {chatError && (
        <div className="px-3 py-1.5 bg-red-500/10 border-b border-red-500/20 text-red-400 text-xs flex items-center justify-between">
          <span>{chatError}</span>
          <button onClick={() => setChatError(null)} className="font-bold ml-2">
            ×
          </button>
        </div>
      )}

      {/* Chat Messages History */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {hasMore && (
          <div className="text-center py-1">
            <button
              onClick={handleLoadMore}
              className="text-[11px] text-emerald-400 hover:underline font-mono"
            >
              Load earlier messages
            </button>
          </div>
        )}

        {messages.length === 0 && !loadingHistory && (
          <div className="text-center py-16 text-gray-500 text-xs">
            <p className="font-medium text-gray-400 mb-1">Welcome to #{room?.name || 'Room'}</p>
            <p>The interior is relaxed. Say hello to fellow verified students.</p>
          </div>
        )}

        {messages.map((msg) => {
          const isMe = msg.authorId === session?.userId;
          const isTombstone = msg.moderationState === 'tombstone_removed';

          return (
            <div
              key={msg.id}
              className={`flex flex-col ${isMe ? 'items-end' : 'items-start'} group`}
            >
              <div className="flex items-baseline space-x-1.5 mb-0.5">
                <span className={`text-[11px] font-semibold ${isMe ? 'text-emerald-400' : 'text-gray-300'}`}>
                  {msg.authorAlias}
                </span>
                <span className="text-[9px] text-gray-500 font-mono">
                  {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>

              <div
                className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-xs leading-relaxed break-words ${
                  isTombstone
                    ? 'bg-[#12141c] text-gray-500 italic border border-gray-800'
                    : isMe
                    ? 'bg-emerald-600/90 text-white rounded-tr-sm shadow-md'
                    : 'bg-[#161922] text-gray-200 border border-[#1f2433] rounded-tl-sm'
                }`}
              >
                <p>{msg.body}</p>

                {msg.attachment && (
                  <div className="mt-2 rounded-lg overflow-hidden border border-black/20">
                    <img
                      src={resolveApiUrl(msg.attachment.url)}
                      alt="Attachment"
                      className="max-h-48 object-cover rounded-lg"
                      loading="lazy"
                    />
                  </div>
                )}
              </div>

              {/* Reaction Pill & Quick Actions */}
              {!isTombstone && (
                <div className="flex items-center space-x-1.5 mt-0.5 opacity-60 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleReaction(msg.id, '❤️')}
                    className="text-[10px] text-gray-400 hover:text-white px-1 py-0.2 rounded hover:bg-[#1f2433]"
                  >
                    ❤️
                  </button>
                  <button
                    onClick={() => handleReaction(msg.id, '🔥')}
                    className="text-[10px] text-gray-400 hover:text-white px-1 py-0.2 rounded hover:bg-[#1f2433]"
                  >
                    🔥
                  </button>
                  <button
                    onClick={() => onOpenReport(msg.id)}
                    className="text-[10px] text-gray-500 hover:text-amber-400 px-1"
                  >
                    Report
                  </button>
                </div>
              )}
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Toolbar & Bar */}
      <div className="p-2.5 bg-[#0c0e17] border-t border-[#1f2433]">
        {/* Selected attachment preview indicator */}
        {selectedAttachmentId && (
          <div className="flex items-center justify-between text-xs bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-2.5 py-1 rounded-lg mb-2">
            <span>Image attachment ready to send</span>
            <button
              onClick={() => setSelectedAttachmentId(null)}
              className="text-gray-400 hover:text-white"
            >
              ×
            </button>
          </div>
        )}

        {/* Slow mode countdown warning */}
        {slowModeSecondsLeft > 0 && (
          <div className="flex items-center space-x-1.5 text-[11px] text-amber-400 font-mono mb-1.5">
            <Clock className="w-3.5 h-3.5" />
            <span>Slow mode active: wait {slowModeSecondsLeft}s</span>
          </div>
        )}

        <form onSubmit={handleSendMessage} className="flex items-center space-x-2">
          {/* Attachment button */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/heic"
            onChange={handleFileSelected}
            className="hidden"
          />
          <button
            type="button"
            disabled={uploadingAttachment}
            onClick={() => fileInputRef.current?.click()}
            className="p-2 rounded-xl text-gray-400 hover:text-emerald-400 hover:bg-[#12141c] transition-colors disabled:opacity-50"
            title="Attach Image"
          >
            <ImageIcon className="w-5 h-5" />
          </button>

          {/* Message Input */}
          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            disabled={slowModeSecondsLeft > 0 || (room?.posting_mode === 'announcement_only' && !isRoomHost)}
            placeholder={
              room?.posting_mode === 'announcement_only' && !isRoomHost
                ? 'Only hosts can post here'
                : slowModeSecondsLeft > 0
                ? `Wait ${slowModeSecondsLeft}s...`
                : 'Message interior...'
            }
            maxLength={2000}
            className="flex-1 bg-[#12141c] border border-[#1f2433] rounded-xl px-3 py-2 text-xs text-gray-100 placeholder-gray-500 focus:outline-none focus:border-emerald-500 disabled:opacity-60"
          />

          {/* Send Button */}
          <button
            type="submit"
            disabled={!inputText.trim() || slowModeSecondsLeft > 0}
            className="p-2 bg-emerald-500 hover:bg-emerald-400 text-black rounded-xl transition-colors disabled:opacity-40 disabled:hover:bg-emerald-500"
          >
            <Send className="w-4 h-4 stroke-[2.5]" />
          </button>
        </form>
      </div>
    </div>
  );
};
